import { AspectRatio } from '@src/types.ts/video';
import { VideoEditorLogger } from '@src/utility/VideoEditorLogger';
import { convertFromPixels } from '@src/videoTranscriptionProcessor/utils';

import { Element } from '../models';
import { LogoPositioner } from '../models/aspectRatio/LogoPositioner';
import { MediaPositioner } from '../models/aspectRatio/MediaPositioner';
import { AbstractCommand } from './AbstractCommand';
import { ElementTypeTransitionCommand } from './ElementTypeTransitionCommand';

import type {
  CompositionElement,
  ImageElement,
  TextElement,
  VideoElement,
} from '../models';
import type { NaturalDimensions, VideoDimensions } from '../types/aspectRatio';
import type { VideoEditor } from '../VideoEditor';

export interface ChangeAspectRatioCommandParams {
  prevVideoDimensions: VideoDimensions;
  newVideoDimensions: VideoDimensions;
  naturalMediaDimensions: Record<
    VideoElement['source'] | ImageElement['source'],
    NaturalDimensions
  >;
  basicTextSetting: Record<string, string>;
}

export class ChangeAspectRatioCommand extends AbstractCommand {
  private params: ChangeAspectRatioCommandParams;

  getName(): string {
    return 'ChangeAspectRatioCommand';
  }

  shouldCreateUndoRedoEntry(): boolean {
    return false;
  }

  private canUndo: boolean = false;

  constructor(editor: VideoEditor, params: ChangeAspectRatioCommandParams) {
    super(editor);
    this.params = params;
  }

  execute(): boolean {
    super.execute();

    if (!this.adjustTimelineDimensions()) return false;

    const success = this.adjustAllElementsToAspectRatio();
    if (!success) return false;

    this.canUndo = true;
    return true;
  }

  undo(): boolean {
    if (!this.canUndo) return false;

    return super.undo();
  }

  getDescription(): string {
    const {
      prevVideoDimensions: prevVideoDimension,
      newVideoDimensions: newVideoDimension,
    } = this.params;
    return `Change aspect ratio from ${prevVideoDimension.aspectRatio} to ${newVideoDimension.aspectRatio}`;
  }

  private adjustTimelineDimensions(): boolean {
    try {
      const { width, height } = this.params.newVideoDimensions;
      this.editor.timeline.setWidth(width);
      this.editor.timeline.setHeight(height);
      return true;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to adjust timeline dimensions',
      });
      return false;
    }
  }

  private adjustAllElementsToAspectRatio(): boolean {
    for (const element of this.editor.timeline.allElements) {
      const isVideo = Element.isVideoElement(element);
      const isImage = Element.isImageElement(element);
      const isComposition = Element.isCompositionElement(element);
      const isLogo = isImage && this.extraElementData.isMarkedAsLogo(element);
      const isText = Element.isTextElement(element);
      const isKaraokeText = isText && element.track === 32;

      // Check if we need to convert black frame compositions to regular images
      if (isComposition && this.shouldConvertBlackFrameComposition(element)) {
        this.convertBlackFrameComposition(element);
      } else if (isLogo) {
        element.update(this.getLogoElementAdjustments(element));
      } else if (isImage || isVideo) {
        element.update(this.getMediaElementAdjustments(element));
      } else if (isText && !isKaraokeText) {
        element.update(this.getTextElementAdjustments(element));
      }
    }

    return true;
  }

  // -------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------

  private getMediaElementAdjustments(
    element: VideoElement | ImageElement,
  ): Record<'x' | 'y' | 'width' | 'height', string> {
    // For elements with fill or contain fit, use default values
    if (element.fit && ['fill', 'contain'].includes(element.fit)) {
      return MediaPositioner.getDefaultPositioning();
    }

    const {
      prevVideoDimensions: prevDimension,
      newVideoDimensions: newDimension,
    } = this.params;

    // Create a media positioner with the dimensions needed for calculations
    const mediaPositioner = new MediaPositioner(prevDimension, newDimension);

    // Get adjusted dimensions from the positioner
    const naturalDimensions = this.getNaturalDimensionsForElement(element);
    return mediaPositioner.getAdjustedDimensions(naturalDimensions);
  }

  private getLogoElementAdjustments(
    element: ImageElement,
  ): Record<'x' | 'y' | 'width' | 'height', string> {
    const { prevVideoDimensions, newVideoDimensions } = this.params;

    // Create a logo positioner with the required parameters
    const logoPositioner = new LogoPositioner(
      prevVideoDimensions.aspectRatio,
      newVideoDimensions.aspectRatio,
      element,
    );

    // Get source aspect ratio from element's natural dimensions
    let sourceAspectRatio = 1;
    const sourceDimensions = this.getNaturalDimensionsForElement(element);
    if (sourceDimensions) {
      sourceAspectRatio = sourceDimensions.width / sourceDimensions.height;
    }

    // Get adjusted dimensions from the logo positioner
    return logoPositioner.getAdjustedDimensions(sourceAspectRatio);
  }

  private getTextElementAdjustments(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    element: TextElement,
  ): Record<string, string> {
    let result = {} as Record<string, string>;

    const { basicTextSetting, newVideoDimensions } = this.params;
    const { width, height } = newVideoDimensions;

    if ('font_size' in basicTextSetting) {
      let value = basicTextSetting['font_size'];
      const vhValue = convertFromPixels(parseInt(value), 'vh', {
        height,
        width,
      });
      value = `${vhValue}vh`;
      result['font_size'] = value;
    }

    if ('y' in basicTextSetting) {
      result['y'] = basicTextSetting['y'];
    }

    if ('width' in basicTextSetting) {
      result['width'] = basicTextSetting['width'];
    }

    return result;
  }

  private getNaturalDimensionsForElement(
    element: VideoElement | ImageElement,
  ): NaturalDimensions | undefined {
    const dimensions = this.params.naturalMediaDimensions[element.source];
    if (dimensions) {
      return dimensions;
    }

    console.warn(
      `Natural dimensions not available for element source: ${element.source}, elementId: ${element.id}`,
    );

    return undefined;
  }

  /**
   * Converts a Black Frame composition to a regular image element
   */
  private convertBlackFrameComposition(element: CompositionElement): void {
    // Use ElementTypeTransitionCommand to handle the conversion
    const transitionCommand = new ElementTypeTransitionCommand(
      this.editor,
      element.id,
      'image',
      { fit: 'cover', locked: false },
    );

    const success = transitionCommand.execute();
    if (success) {
      // Get the converted element and apply aspect ratio adjustments
      const convertedElement = this.editor.timeline.getElement(element.id);
      if (convertedElement && Element.isImageElement(convertedElement)) {
        convertedElement.update(
          this.getMediaElementAdjustments(convertedElement),
        );
      }
    } else {
      VideoEditorLogger.logCommand(
        this,
        'error',
        `Failed to convert Black Frame composition ${element.id} using ElementTypeTransitionCommand`,
      );
    }
  }

  /**
   * Determines if a Black Frame composition should be converted to a regular image
   * when changing from 16:9 to non-16:9 aspect ratios
   */
  private shouldConvertBlackFrameComposition(
    element: CompositionElement,
  ): boolean {
    const { prevVideoDimensions, newVideoDimensions } = this.params;

    // Only convert if we're changing FROM 16:9 TO non-16:9
    if (prevVideoDimensions.aspectRatio !== AspectRatio.AR_16_9) {
      return false;
    }

    if (newVideoDimensions.aspectRatio === AspectRatio.AR_16_9) {
      return false;
    }

    // Check if this composition looks like a Black Frame composition
    return element.isBlackFrameComposition();
  }
}
